import type { CSSProperties } from 'react';

/**
 * Estilos del carrito extraidos del componente para que las vistas queden legibles.
 * Las reglas que necesitan :hover, :active o media queries viven en `index.css`
 * bajo el prefijo `.cart-`.
 */
export const cartStyles = {
  drawerBody: {
    padding: '16px 16px calc(16px + env(safe-area-inset-bottom, 0px))',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'var(--color-paper)',
  } satisfies CSSProperties,

  drawerHeader: {
    borderBottom: '1px solid var(--color-border)',
    padding: '14px 16px',
  } satisfies CSSProperties,

  /* Hoja inferior en movil: ocupa solo lo que necesita su contenido */
  sheetWrapper: {
    height: 'auto',
    maxHeight: '88dvh',
    borderRadius: 'var(--radius-2xl) var(--radius-2xl) 0 0',
    overflow: 'hidden',
  } satisfies CSSProperties,

  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
  } satisfies CSSProperties,

  headerTitle: {
    margin: 0,
    fontWeight: 800,
    fontFamily: 'var(--font-display)',
    letterSpacing: '-0.03em',
    color: 'var(--color-ink)',
    fontSize: 18,
    lineHeight: 1.2,
  } satisfies CSSProperties,

  scrollArea: {
    flex: 1,
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    margin: '0 -4px',
    padding: '0 4px',
  } satisfies CSSProperties,

  /* ---------- Linea del carrito ---------- */
  line: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginBottom: 10,
    padding: 12,
    backgroundColor: 'var(--color-card)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
    boxShadow: 'var(--shadow-1)',
  } satisfies CSSProperties,

  lineTop: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
  } satisfies CSSProperties,

  thumb: {
    width: 52,
    height: 52,
    flexShrink: 0,
    borderRadius: 'var(--radius-sm)',
    objectFit: 'cover',
    display: 'block',
    backgroundColor: 'var(--color-surface)',
  } satisfies CSSProperties,

  thumbFallback: {
    width: 52,
    height: 52,
    flexShrink: 0,
    borderRadius: 'var(--radius-sm)',
    backgroundColor: 'var(--color-surface)',
    color: 'var(--color-muted)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'var(--font-display)',
    fontWeight: 800,
    fontSize: 18,
  } satisfies CSSProperties,

  lineName: {
    fontSize: 14,
    fontWeight: 700,
    color: 'var(--color-ink)',
    fontFamily: 'var(--font-display)',
    lineHeight: 1.3,
    letterSpacing: '-0.02em',
    overflowWrap: 'anywhere',
  } satisfies CSSProperties,

  lineModifier: {
    fontSize: 12,
    color: 'var(--color-ink-soft)',
    fontFamily: 'var(--font-body)',
    lineHeight: 1.45,
    overflowWrap: 'anywhere',
  } satisfies CSSProperties,

  lineModifierExtra: {
    color: 'var(--color-muted)',
    fontFamily: 'var(--font-mono)',
    fontSize: 11,
  } satisfies CSSProperties,

  lineNote: {
    fontSize: 12,
    margin: 0,
    fontStyle: 'italic',
    color: 'var(--color-muted)',
    fontFamily: 'var(--font-body)',
    overflowWrap: 'anywhere',
  } satisfies CSSProperties,

  offerTag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    alignSelf: 'flex-start',
    maxWidth: '100%',
    backgroundColor: 'var(--color-accent-soft)',
    border: '1px solid var(--color-accent)',
    borderRadius: 'var(--radius-full)',
    padding: '3px 9px',
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--color-accent)',
    fontFamily: 'var(--font-body)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  } satisfies CSSProperties,

  lineBottom: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 10,
  } satisfies CSSProperties,

  linePrice: {
    fontSize: 14,
    fontWeight: 700,
    color: 'var(--color-ink)',
    fontFamily: 'var(--font-display)',
    letterSpacing: '-0.02em',
    fontVariantNumeric: 'tabular-nums',
  } satisfies CSSProperties,

  linePriceStrike: {
    fontSize: 11,
    color: 'var(--color-muted)',
    fontFamily: 'var(--font-mono)',
    textDecoration: 'line-through',
  } satisfies CSSProperties,

  linePriceNet: {
    fontSize: 14,
    fontWeight: 800,
    color: 'var(--color-accent)',
    fontFamily: 'var(--font-display)',
    fontVariantNumeric: 'tabular-nums',
  } satisfies CSSProperties,

  /* ---------- Resumen ---------- */
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: 12,
    marginBottom: 6,
  } satisfies CSSProperties,

  summaryLabel: {
    fontSize: 13,
    color: 'var(--color-muted)',
    fontFamily: 'var(--font-body)',
  } satisfies CSSProperties,

  summaryValue: {
    fontSize: 13,
    color: 'var(--color-ink)',
    fontFamily: 'var(--font-mono)',
    fontVariantNumeric: 'tabular-nums',
  } satisfies CSSProperties,

  savingRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
    backgroundColor: 'var(--color-accent-soft)',
    border: '1px solid var(--color-accent)',
    borderRadius: 'var(--radius-md)',
    padding: '7px 10px',
    color: 'var(--color-accent)',
    fontFamily: 'var(--font-body)',
    fontSize: 12.5,
    fontWeight: 700,
  } satisfies CSSProperties,

  totalLabel: {
    fontSize: 13.5,
    color: 'var(--color-ink)',
    fontFamily: 'var(--font-body)',
    fontWeight: 700,
  } satisfies CSSProperties,

  totalValue: {
    fontSize: 20,
    fontWeight: 800,
    color: 'var(--color-ink)',
    fontFamily: 'var(--font-display)',
    letterSpacing: '-0.03em',
    lineHeight: 1.1,
    fontVariantNumeric: 'tabular-nums',
  } satisfies CSSProperties,

  vesValue: {
    fontSize: 12,
    fontWeight: 700,
    color: 'var(--color-accent)',
    fontFamily: 'var(--font-mono)',
    fontVariantNumeric: 'tabular-nums',
  } satisfies CSSProperties,

  checkoutButton: {
    backgroundColor: 'var(--color-accent)',
    borderColor: 'var(--color-accent)',
    color: 'var(--color-accent-contrast)',
    borderRadius: 'var(--radius-full)',
    fontWeight: 700,
    height: 52,
    fontFamily: 'var(--font-body)',
    fontSize: 15,
    letterSpacing: '-0.01em',
    boxShadow: '0 6px 16px rgba(194,65,12,0.28)',
  } satisfies CSSProperties,

  footnote: {
    display: 'block',
    textAlign: 'center',
    fontSize: 11,
    color: 'var(--color-muted)',
    marginTop: 8,
    fontFamily: 'var(--font-body)',
  } satisfies CSSProperties,

  closedNotice: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '12px 14px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-error)',
    backgroundColor: 'var(--color-error-soft)',
    color: 'var(--color-error)',
    fontFamily: 'var(--font-body)',
  } satisfies CSSProperties,

  emptyState: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: 12,
    padding: '40px 0',
    textAlign: 'center',
  } satisfies CSSProperties,
};
